import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import FileUpload from '../../../components/ui/FileUpload';

const ColorsTab = ({ colors, onAdd, onEdit, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    hexCode: '#000000',
    swatchImage: '',
    swatchImageAlt: '',
    category: 'base',
    usageCount: 0
  });

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        hexCode: '#000000',
        swatchImage: '',
        swatchImageAlt: '',
        category: 'base',
        usageCount: 0
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (editingItem) {
      onEdit({ ...formData, id: editingItem?.id });
    } else {
      onAdd({ ...formData, id: Date.now() });
    }
    handleCloseModal();
  };

  const handleDelete = (item) => {
    if (window.confirm(`Delete "${item?.name}"? This will affect ${item?.usageCount} companies.`)) {
      onDelete(item?.id);
    }
  };

  const categoryColors = {
    base: colors?.filter(c => c?.category === 'base'),
    flake: colors?.filter(c => c?.category === 'flake'),
    accent: colors?.filter(c => c?.category === 'accent')
  };

  const renderColorGrid = (categoryColors, categoryName) => (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-text-primary capitalize">
        {categoryName} Colors ({categoryColors?.length})
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {categoryColors?.map((color) => (
          <div
            key={color?.id}
            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
          >
            <div className="aspect-square bg-muted overflow-hidden relative">
              {color?.swatchImage ? (
                <Image
                  src={color?.swatchImage}
                  alt={color?.swatchImageAlt}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full"
                  style={{ backgroundColor: color?.hexCode }}
                />
              )}
            </div>
            <div className="p-3 space-y-2">
              <div>
                <h4 className="font-medium text-sm text-text-primary truncate">
                  {color?.name}
                </h4>
                <p className="text-xs text-text-secondary font-mono">{color?.hexCode}</p>
              </div>
              <div className="flex items-center gap-1 text-xs text-text-secondary">
                <Icon name="Building2" size={12} />
                <span>{color?.usageCount}</span>
              </div>
              <div className="flex items-center gap-1 pt-2 border-t border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenModal(color)}
                  iconName="Edit2"
                  iconSize={12}
                  className="flex-1 text-xs"
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(color)}
                  iconName="Trash2"
                  iconSize={12}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Colors</h2>
          <p className="text-sm text-text-secondary mt-1">
            Manage default color options for base, flake, and accent selections
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => handleOpenModal()}
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Add Color
        </Button>
      </div>
      <div className="space-y-8">
        {renderColorGrid(categoryColors?.base, 'base')}
        {renderColorGrid(categoryColors?.flake, 'flake')}
        {renderColorGrid(categoryColors?.accent, 'accent')}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary">
                {editingItem ? 'Edit Color' : 'Add Color'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-text-secondary hover:text-text-primary transition-colors duration-150"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                label="Color Name"
                type="text"
                placeholder="e.g., Midnight Blue"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Hex Code
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData?.hexCode}
                      onChange={(e) => setFormData({ ...formData, hexCode: e?.target?.value })}
                      className="w-12 h-10 rounded border border-input cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={formData?.hexCode}
                      onChange={(e) => setFormData({ ...formData, hexCode: e?.target?.value })}
                      placeholder="#000000"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Category
                  </label>
                  <select
                    value={formData?.category}
                    onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-ring"
                    required
                  >
                    <option value="base">Base Color</option>
                    <option value="flake">Flake Color</option>
                    <option value="accent">Accent Color</option>
                  </select>
                </div>
              </div>

              <FileUpload
                label="Swatch Image (Optional)"
                accept="image/*"
                description="Upload a texture swatch for more accurate representation"
                onUpload={(file) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setFormData({
                      ...formData,
                      swatchImage: reader?.result,
                      swatchImageAlt: `${formData?.name} color swatch showing texture and finish`
                    });
                  };
                  reader?.readAsDataURL(file);
                }}
              />

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" variant="default" className="flex-1">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorsTab;